import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Flex, Input, Select, Typography } from 'antd'
import {
  CheckCircleOutlined,
  DisconnectOutlined,
  EditOutlined,
  GiftOutlined,
  HistoryOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
  ThunderboltOutlined,
  UnlockOutlined,
  UserDeleteOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PERM } from '@/shared/auth/partnerPermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { CASHDESK_PATH, cashdeskLink } from '@/shared/lib/cashdeskLink'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'
import type { RowAction } from '@/shared/ui/RowActions'
import { SubscriberHistoryDrawer, type HistorySubscriber } from '@/features/history/SubscriberHistoryDrawer'
import { GrantIssueModal } from '@/features/subscriber/GrantIssueModal'
import { SubscriberActionDialog, type ActionKind } from '@/features/subscriber/SubscriberActionDialog'
import { SubscriberCard } from '@/features/subscriber/SubscriberCard'
import { SubscriberFormModal } from '@/features/subscriber/SubscriberFormModal'
import { SubscriberList } from '@/features/subscriber/SubscriberList'
import {
  SUBSCRIBER_STATES,
  fetchSubscribers,
  type Subscriber,
  type SubscriberRow,
} from '@/features/subscriber/subscriberApi'
import { subscriberKeys } from '@/features/subscriber/subscriberKeys'
import type { Money } from '@/shared/lib/money'

/** Yozayotganda har harfda so'rov ketmasin (admin `DataTable` da o'lchangan) */
const DEBOUNCE_MS = 350

const DEFAULT_SIZE = 20

/**
 * «Abonentlar» — RESELLER uchun o'z shoxidagi abonentlar.
 *
 * <h2>⚠️ Doira SERVER tomonida</h2>
 * Reseller `SUBTREE` doirasida ishlaydi (ADR-029 §2) va `AccessGuard`
 * har so'rovda predikat qo'yadi. Panelda `resellerId` filtri YO'Q va
 * qo'shilmaydi: takrorlangan doira ikkinchi haqiqat manbai bo'lardi
 * (04-ROLES.md §5).
 *
 * <h2>⚠️ NEGA AMALLAR `can(...)` BILAN FILTRLANMAYDI</h2>
 * Sherikning huquq to'plami KODDA qotirilgan va DINAMIK EMAS
 * (ADR-029 §2, `PartnerKind.java`). `subscriber.read` faqat bitta
 * to'plamda bor — `RESELLER` da, va o'sha to'plam ayni paytda
 * `subscriber.update`, `subscriber.delete`, `subscriber.block`,
 * `subscriber.password.set`, `subscriber.session.disconnect`,
 * `subscriber.ratelimit.set` va `grant.issue` ni ham beradi. Ya'ni bu
 * SAHIFANI OCHA OLGAN odamda quyidagi amallarning HAMMASI bor —
 * `can(...)` tekshiruvi har doim rost bo'lardi va u faqat «tekshiruv
 * bor» degan taassurot yasardi.
 *
 * ⚠️ «Pul ham oladigan reseller» bayrog'i to'plamga QO'SHADI
 * (`subscriber.directory`, `agent.fund`), olib tashlamaydi — ya'ni bu
 * xulosa u holatda ham to'g'ri qoladi.
 *
 * ⚠️ Haqiqiy himoya baribir serverda: har so'rov qayta tekshiriladi
 * (04-ROLES.md §5). Bu yerdagi qaror faqat «bosib bo'lmaydigan tugma
 * ko'rsatmaslik» haqida.
 *
 * <h2>⚠️ IKKI AMAL KASSAGA YO'NALTIRILADI — VA FAQAT HUQUQ BO'LSA</h2>
 * Egasi talabi (2026-08-21): balans va paket BITTA joydan boshqarilsin.
 * Shuning uchun `fund` va `grant` kassani SHU ABONENT tanlangan holda
 * ochadi (`shared/lib/cashdeskLink`).
 *
 * ⚠️ LEKIN BU YERDA `can(...)` MAJBURIY va yuqoridagi xulosaga zid
 * emas. Yuqoridagi izoh ABONENT amallari haqida — ular `subscriber.read`
 * bilan birga keladi. `subscriber.directory` va `agent.fund` esa
 * ALOHIDA to'plamdan (`PartnerKind.CASH_DESK`) va ular kassasiz
 * resellerda YO'Q:
 *
 *   · kassasiz reseller → «Balansni to'ldirish» UMUMAN chizilmaydi
 *     (u `agent.fund` da 403 olardi), `grant` esa avvalgidek shu
 *     ekrandagi oynada qoladi (`GrantIssueModal`);
 *   · kassali sherik → ikkalasi ham kassaga yo'naltiradi.
 *
 * ⚠️ Shart SHERIK TURIGA qaramaydi (`kind === 'RESELLER'` YO'Q):
 * «pul ham oladigan reseller» aynan o'sha tekshiruvdan chetda qolardi
 * (ADR-029 §2, `StartScreen` bilan bir xil qoida).
 */
export function SubscribersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()

  /**
   * Kassa ekrani shu sherikda BORMI.
   *
   * ⚠️ IKKALA huquq ham so'raladi. Yo'naltirish faqat ekran ochilganda
   * ma'noli (`subscriber.directory` — `StartScreen` bilan ayni shart),
   * to'ldirish tugmasi esa faqat `agent.fund` bilan ishlaydi. Bittasini
   * tekshirib qo'yish tugmani bosilganda bo'sh ekranga yoki 403 ga olib
   * borardi.
   */
  const canCashdesk = can(PERM.subscriberDirectory)
  const canFund = canCashdesk && can(PERM.agentFund)
  const canHistory = can(PERM.subscriberHistoryView)

  const [text, setText] = useState('')
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(DEFAULT_SIZE)

  const [selected, setSelected] = useState<SubscriberRow | null>(null)
  const [historyOf, setHistoryOf] = useState<HistorySubscriber | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formTarget, setFormTarget] = useState<Subscriber | null>(null)
  const [grantTarget, setGrantTarget] = useState<SubscriberRow | null>(null)
  const [dialog, setDialog] = useState<{
    kind: ActionKind
    subscriber: Subscriber
    /** ⚠️ `null` = NOMA'LUM, nol emas — ogohlantirish o'shanda YOZILMAYDI */
    balance: Money | null
  } | null>(null)
  const [cardRateLimit, setCardRateLimit] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearch(text.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [text])

  // ⚠️ Filtr o'zgarganda sahifa BOSHIGA qaytadi: aks holda uchinchi
  // sahifada turgan odam qidiruvdan keyin bo'sh ro'yxat ko'rardi va uni
  // «hech narsa topilmadi» deb o'qirdi.
  useEffect(() => {
    setPage(0)
  }, [search, stateFilter])

  const listKey = { search, state: stateFilter, page, size }
  const list = useQuery({
    queryKey: subscriberKeys.list(listKey),
    queryFn: ({ signal }) => fetchSubscribers(listKey, signal),
    placeholderData: keepPreviousData,
  })

  /*
    ⚠️ OCHIQ KARTOCHKA VA PAKET OYNASI YANGILANADI.

    Amaldan keyin kesh bekor qilinadi va ro'yxat yangi holat bilan
    qaytadi — kartochka esa shu yerdagi holatda turadi va ESKI qatorni
    ko'rsatib qolardi: bloklangan abonent ustida hali ham «Faol» tegi.
    Naqsh kassa ekranidagi bilan bir xil (`SubscriberSearch.onRefresh`).

    ⚠️ Topilmasa ESKISI qoladi (`?? prev`): abonent filtrdan chiqib
    ketishi mumkin (masalan yopilgani), oynani esa o'sha payt yopib
    yuborish foydalanuvchi ko'rmagan natijani ham olib ketardi.
  */
  useEffect(() => {
    const fresh = list.data?.rows
    if (!fresh) return
    const refresh = (prev: SubscriberRow | null): SubscriberRow | null =>
      prev === null
        ? null
        : (fresh.find((row) => row.subscriber.id === prev.subscriber.id) ?? prev)
    setSelected(refresh)
    setGrantTarget(refresh)
  }, [list.data])

  const openDialog = useCallback((kind: ActionKind, row: SubscriberRow) => {
    setDialog({ kind, subscriber: row.subscriber, balance: row.balance })
  }, [])

  /**
   * Amallar ro'yxati — BIR JOYDA yasaladi va ikki joyda chiziladi
   * (jadval qatoridagi uch nuqta menyusi va kartochkadagi tugmalar).
   *
   * ⚠️ Yopilgan hisobda deyarli hamma narsa o'chiriladi: server ham
   * ularni rad etadi (`SubscriberState.canTransact()`), lekin sababni
   * OLDINDAN ko'rsatish bitta bekor urinishni tejaydi
   * (11-SHERIK-KABINETI-UI.md §7 dagi oxirgi qator).
   */
  const actionsFor = useCallback(
    (row: SubscriberRow): readonly RowAction[] => {
      const s = row.subscriber
      const closed = s.state === 'CLOSED'
      const blocked = s.state === 'BLOCKED'
      /*
        ⚠️ FAOLLASHTIRISH — FAQAT `NEW` va `SUSPENDED`.

        `BLOCKED` da tugma KO'RSATILMAYDI, garchi holat mashinasi
        `BLOCKED → ACTIVE` ni ruxsat etsa ham: server bu yo'lni ATAYLAB
        rad etadi va `200 + FAILED` qaytaradi
        («Abonent operator tomonidan bloklangan — faollashtirib
        bo'lmaydi», `SubscriberServiceImpl.activate`). Blokni ochish
        alohida huquq (`subscriber.block`) ostidagi intizomiy amal va
        u shu menyuda «Blokdan chiqarish» bo'lib turibdi. Bosiladigan,
        lekin har doim rad etiladigan tugma operatorni chalg'itardi.

        ⚠️ `ACTIVE` da ham yo'q — ma'nosi yo'q.
      */
      const activatable = s.state === 'NEW' || s.state === 'SUSPENDED'

      return [
        {
          key: 'edit',
          label: t('common.edit'),
          icon: <EditOutlined />,
          disabled: closed,
          onClick: () => {
            setFormTarget(s)
            setFormOpen(true)
          },
        },
        {
          key: 'activate',
          label: t('subscribers.action.activate.title'),
          icon: <CheckCircleOutlined />,
          hidden: !activatable,
          onClick: () => openDialog('activate', row),
        },
        /*
          ⚠️ «Balansni to'ldirish» — AMAL EMAS, YO'NALTIRISH.

          Pul kassirning depozitidan ko'chadi va bu oqim allaqachon
          kassada (`FundPanel`: shift, depozit qoldig'i, idempotentlik
          kaliti, qisman natija). Uni bu yerda ikkinchi marta yozish
          ikkita haqiqat manbai bo'lardi. Shuning uchun bu yerdagi ish
          bitta: abonentni tanlangan holda o'sha ekranga olib borish.
        */
        {
          key: 'fund',
          label: t('subscribers.action.fund'),
          icon: <WalletOutlined />,
          hidden: !canFund,
          disabled: closed,
          onClick: () => navigate(cashdeskLink(s.login, 'fund')),
        },
        {
          key: 'grant',
          label: t('subscribers.action.grant'),
          icon: <GiftOutlined />,
          disabled: closed,
          // ⚠️ Kassali sherikda BIRLASHGAN oqim ochiladi: balansi
          // yetmagan abonentga `GrantPanel` o'zi to'ldirishni taklif
          // qiladi. Kassasiz resellerda esa kassa ekrani UMUMAN yo'q,
          // shuning uchun u yerda oyna qoladi.
          onClick: () =>
            canCashdesk ? navigate(cashdeskLink(s.login, 'grant')) : setGrantTarget(row),
        },
        {
          key: 'history',
          label: t('history.open'),
          icon: <HistoryOutlined />,
          hidden: !canHistory,
          onClick: () => setHistoryOf({ id: s.id, login: s.login }),
        },
        /*
          ⚠️ IKKI PAROL — IKKI AMAL, va ular YONMA-YON turadi.

          Abonentda ikki mustaqil parol bor: internetga ulanish
          (RADIUS/PPPoE) va mijoz kabinetiga kirish (`xsib-selfcare`).
          Ilgari menyuda faqat birinchisi bor edi va u shunchaki
          «Parol» deb nomlanardi — endi ikkinchisi qo'shilgach bu nom
          ARALASHTIRARDI: operator kabinet uchun so'ralgan parolni
          RADIUS'ga yozib, mijozning INTERNETINI o'chirib qo'yardi va
          nosozlik faqat mijoz qo'ng'iroq qilganda bilinardi.

          Shuning uchun uchta ajratgich birdan: nom («Internet paroli»
          / «Kabinet paroli»), belgi (kalit / qulf) va oynadagi matn.
          Bittasi yetarli emas — menyuda belgi va nom yonma-yon
          o'qiladi, oyna esa tasdiqdan oldin oxirgi imkoniyat.
        */
        {
          key: 'password',
          label: t('subscribers.action.password.title'),
          icon: <KeyOutlined />,
          disabled: closed,
          onClick: () => openDialog('password', row),
        },
        {
          key: 'selfcarePassword',
          label: t('subscribers.action.selfcarePassword.title'),
          icon: <LockOutlined />,
          // ⚠️ Yopilgan hisobda o'chirilgan — qolgan amallar bilan bir
          // xil qoida: yopilgan abonent kabinetga ham kirmaydi.
          disabled: closed,
          onClick: () => openDialog('selfcarePassword', row),
        },
        {
          key: 'rateLimit',
          label: t('subscribers.action.rateLimit.title'),
          icon: <ThunderboltOutlined />,
          disabled: closed,
          onClick: () => openDialog('rateLimit', row),
        },
        {
          key: 'disconnect',
          label: t('subscribers.action.disconnect.title'),
          icon: <DisconnectOutlined />,
          disabled: closed,
          onClick: () => openDialog('disconnect', row),
        },
        // ⚠️ «Bloklash» va «Blokdan chiqarish» BIR VAQTDA ko'rinmaydi:
        // ikkalasi bir menyuda turganda qaysi biri hozir amalda ekani
        // faqat holat tegiga qarab bilinardi.
        {
          key: 'block',
          label: t('subscribers.action.block.title'),
          icon: <StopOutlined />,
          danger: true,
          hidden: blocked,
          disabled: closed,
          onClick: () => openDialog('block', row),
        },
        {
          key: 'unblock',
          label: t('subscribers.action.unblock.title'),
          icon: <UnlockOutlined />,
          hidden: !blocked,
          onClick: () => openDialog('unblock', row),
        },
        {
          key: 'close',
          label: t('subscribers.action.close.title'),
          icon: <UserDeleteOutlined />,
          danger: true,
          hidden: closed,
          onClick: () => openDialog('close', row),
        },
      ]
    },
    [t, openDialog, canCashdesk, canFund, canHistory, navigate],
  )

  const cardActions = useMemo(
    () => (selected === null ? [] : actionsFor(selected)),
    [selected, actionsFor],
  )

  const rows = list.data?.rows ?? []
  const total = list.data?.total ?? 0
  const filtered = search !== '' || stateFilter !== null

  return (
    <Flex vertical gap={16}>
      <Flex align="flex-start" justify="space-between" gap={12} wrap>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('subscribers.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('subscribers.subtitle')}</Typography.Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setFormTarget(null)
            setFormOpen(true)
          }}
        >
          {t('subscribers.create')}
        </Button>
      </Flex>

      {/*
        ⛔ DOIRA MATN BILAN TUSHUNTIRILADI — kod O'ZGARMAYDI (egasi
        qarori, 2026-08-24).

        <h2>O'LCHANGAN HOLAT (Staging, `reseller_nurbek`)</h2>
        Kassa qidiruvi 7 ta abonentni topadi, bu ro'yxatda esa 2 tasi
        bor. Kassadan `nurbek1999` ga pul to'ldirilgan va paket
        berilgan — u BU RO'YXATDA UMUMAN YO'Q.

        Ikkala ekran ham TO'G'RI ishlayapti va sabab tuzilmaviy:
          · kassa   — `subscriber.directory`, BUTUN tashkilot bo'yicha
            (ADR-029 §3 dagi qisqartirilgan katalog);
          · ro'yxat — `subscriber.read` + `SUBTREE` doirasi, ya'ni
            sherikning O'Z shoxi.
        Pul berish abonentni shoxga KO'CHIRMAYDI — u boshqa tugunda
        qolaveradi.

        ⚠️ Shuning uchun bu yerdagi bo'shliq NUQSON EMAS, lekin u
        nuqsondek ko'rinadi: sherik «mijozlarimning yarmi yo'qolibdi»
        deb o'qiydi. Matn aynan shu xulosani to'sadi.

        ⛔ MATN KASSA HUQUQIGA QARAB O'ZGARADI. Kassasiz resellerda
        (`cash_desk = f`) kassa ekranining O'ZI yo'q — unga «kassada
        qidiring» deyish mavjud bo'lmagan bo'limga yuborardi.
      */}
      <Alert
        type="info"
        showIcon
        message={t('subscribers.scope.title')}
        description={
          <Flex vertical gap={6} align="flex-start">
            <span>
              {canCashdesk ? t('subscribers.scope.text') : t('subscribers.scope.textNoCashdesk')}
            </span>
            {canCashdesk ? (
              <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
                onClick={() => navigate(CASHDESK_PATH)}>
                {t('subscribers.scope.toCashdesk')}
              </Button>
            ) : null}
          </Flex>
        }
      />

      <Flex gap={8} wrap>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('subscribers.searchPlaceholder')}
          // ⚠️ `fontSize: 16` — bezak emas, XULQ: iOS Safari undan kichik
          // matnli maydonga fokus tushganda sahifani avtomatik
          // kattalashtiradi va orqaga qaytarmaydi.
          style={{ flex: '1 1 260px', minWidth: 200, fontSize: 16 }}
        />
        <Select
          value={stateFilter}
          onChange={setStateFilter}
          allowClear
          placeholder={t('subscribers.columns.state')}
          style={{ minWidth: 160 }}
          options={SUBSCRIBER_STATES.map((value) => ({
            value,
            label: t(`subscriberState.${value}`),
          }))}
        />
      </Flex>

      {list.isError ? <LoadError titleKey="subscribers.loadFailed" error={list.error} /> : null}

      {list.isSuccess && rows.length === 0 ? (
        <EmptyState
          title={filtered ? t('subscribers.emptyFiltered') : t('subscribers.empty')}
          description={filtered ? t('subscribers.emptyFilteredHint') : t('subscribers.emptyHint')}
          {...(filtered
            ? {}
            : {
                actionLabel: t('subscribers.create'),
                onAction: () => {
                  setFormTarget(null)
                  setFormOpen(true)
                },
              })}
        />
      ) : (
        <SubscriberList
          rows={rows}
          total={total}
          page={page}
          size={size}
          loading={list.isFetching}
          onPageChange={(nextPage, nextSize) => {
            setPage(nextPage)
            setSize(nextSize)
          }}
          onOpen={setSelected}
          actionsFor={actionsFor}
        />
      )}

      <SubscriberCard
        row={selected}
        onClose={() => setSelected(null)}
        actions={cardActions}
        onRateLimitLoaded={setCardRateLimit}
      />

      <SubscriberHistoryDrawer subscriber={historyOf} onClose={() => setHistoryOf(null)} />

      <SubscriberFormModal
        open={formOpen}
        subscriber={formTarget}
        onClose={() => setFormOpen(false)}
      />

      <GrantIssueModal
        subscriber={grantTarget?.subscriber ?? null}
        balance={grantTarget?.balance ?? null}
        onClose={() => setGrantTarget(null)}
      />

      <SubscriberActionDialog
        kind={dialog?.kind ?? null}
        subscriber={dialog?.subscriber ?? null}
        balance={dialog?.balance ?? null}
        // ⚠️ Faqat kartochka ochiq bo'lganda ma'lum: ro'yxat qatorida
        // `rateLimitOverride` yuklanmagan va uni taxmin qilish
        // ekranga yolg'on qiymat chiqarardi.
        rateLimitOverride={
          dialog !== null && selected?.subscriber.id === dialog.subscriber.id
            ? cardRateLimit
            : null
        }
        onClose={() => setDialog(null)}
      />
    </Flex>
  )
}
